import { useEffect, useLayoutEffect, useRef, useState, type TextareaHTMLAttributes } from 'react'
import { cn } from '../lib/cn.js'
import { centsOf, dollarsOf } from '../lib/money.js'
import { Input } from './ui.js'

/*
 * Two inputs the proposal canvas made and the rest of the app wanted. They
 * live here rather than in the canvas's files because importing one from
 * there brought the whole canvas into the first load with it.
 */

/** A textarea that grows with its words, so the page never scrolls inside itself. */
export function AutoTextarea({
  label,
  value,
  placeholder,
  className,
  autoFocus,
  onChange,
  onRange,
  ...rest
}: {
  label: string
  value: string
  placeholder?: string
  className?: string
  autoFocus?: boolean
  onChange: (value: string) => void
  onRange?: (range: { start: number; end: number } | null) => void
} & Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'onSelect' | 'onKeyUp' | 'onMouseUp' | 'className' | 'rows' | 'placeholder' | 'autoFocus'
>) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }, [value])

  useLayoutEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const report = () => {
    const node = ref.current
    if (!node || !onRange) return
    onRange(node.selectionEnd > node.selectionStart ? { start: node.selectionStart, end: node.selectionEnd } : null)
  }

  return (
    <textarea
      {...rest}
      ref={ref}
      aria-label={label}
      rows={1}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onSelect={report}
      onKeyUp={report}
      onMouseUp={report}
      className={cn(
        'block w-full resize-none overflow-hidden bg-transparent placeholder:text-ink-300 focus:outline-none',
        className,
      )}
    />
  )
}

/**
 * Dollars typed as text. The value is only ever minor units, so this keeps
 * what was typed while the field has focus and reformats on the way out —
 * otherwise "12." would be rewritten as "12" under the cursor.
 */
export function PriceInput({
  id,
  value,
  onChange,
  placeholder,
  className,
  label,
}: {
  id?: string
  value: number | null
  onChange: (cents: number | null) => void
  placeholder?: string
  className?: string
  label?: string
}) {
  const [text, setText] = useState(dollarsOf(value))
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setText(dollarsOf(value))
  }, [value])

  return (
    <Input
      id={id}
      aria-label={label}
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      className={className}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        setText(dollarsOf(value))
      }}
      onChange={(event) => {
        setText(event.target.value)
        onChange(centsOf(event.target.value))
      }}
    />
  )
}
